import type { IconProp } from '@fortawesome/fontawesome-svg-core'
import type { ReactNode } from 'react'
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { Component } from 'react'
import { emergencyReset } from '../util/serviceWorkerUtils'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
}

class ServiceWorkerErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if this is the specific service worker cache error
    const isServiceWorkerError = error.message.includes('expected expression, got \'<\'')
      || error.message.includes('Unexpected token \'<\'')
      || error.message.includes('SyntaxError')

    return {
      hasError: isServiceWorkerError,
      error: isServiceWorkerError ? error : undefined,
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error for debugging
    console.error('Service Worker Error:', error, errorInfo)

    // Check if this looks like a service worker cache issue
    const isServiceWorkerError = error.message.includes('expected expression, got \'<\'')
      || error.message.includes('Unexpected token \'<\'')
      || error.message.includes('SyntaxError')

    if (isServiceWorkerError) {
      this.setState({ hasError: true, error })
    }
  }

  handleReset = async () => {
    try {
      await emergencyReset()
    }
    catch (error) {
      console.error('Emergency reset failed:', error)
      // Force reload as last resort
      window.location.reload()
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          data-bs-theme="dark"
          style={{
            width: '100vw',
            height: '100vh',
            position: 'fixed',
            top: '0',
            left: '0',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div className="card card-danger" style={{ maxWidth: '500px', margin: '20px' }}>
            <div className="card-header">
              <h3 className="card-title">Cache fout gedetecteerd</h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-2">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle as IconProp}
                    size="2x"
                    className="text-danger"
                    style={{
                      position: 'relative',
                      top: '50%',
                      transform: 'translatey(-50%)',
                    }}
                  />
                </div>
                <div className="col-10">
                  <h4 className="text-danger">Service Worker Cache Probleem</h4>
                  <p className="text-danger">
                    Er is een probleem met de cache van de applicatie.
                    Dit gebeurt soms na updates. Klik op "Reset" om het probleem op te lossen.
                  </p>
                  <small className="text-muted">
                    Fout:
                    {' '}
                    {this.state.error?.message}
                  </small>
                </div>
              </div>
            </div>
            <div className="card-footer">
              <button
                className="btn btn-danger"
                onClick={this.handleReset}
              >
                Reset Cache en Herlaad
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ServiceWorkerErrorBoundary
